import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useTonConnectUI, useTonWallet } from "@tonconnect/ui-react";
import { useUser } from "../hooks/useUser";
import ConfettiCanvas from "./ConfettiCanvas"; // Импортируем компонент ConfettiCanvas

// Константа для управления видимостью TON-платежей
// Отключено согласно политике Telegram Mini Apps - криптовалютные платежи запрещены для цифровых товаров
// Включить/выключить в случае изменения политики Telegram в будущем
const ENABLE_TON_PAYMENTS = false;

// Старый lifetime popup: разовая покупка Stars (STARS_PRICE) → бессрочный доступ.
// Показывается когда subscription_enabled = false в админке.
const LifetimePremiumModal = ({ isOpen, onClose }) => {
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const { user, hasAccess } = useUser();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [prices, setPrices] = useState({ usdt: 10, stars: 1000 });
  const [exchangeRate, setExchangeRate] = useState({
    usdtToTonRate: 0.18,
    tonPrice: 5.5,
  });

  // Закрываем модал если пользователь получил доступ
  useEffect(() => {
    if (isOpen && hasAccess()) {
      onClose();
    }
  }, [isOpen, user, hasAccess, onClose]);

  // Загружаем цены и курс обмена из API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Загружаем цены
        const pricesResponse = await fetch("/api/payment/prices");
        if (pricesResponse.ok) {
          const pricesData = await pricesResponse.json();
          setPrices(pricesData);
        }

        // Загружаем курс обмена
        const rateResponse = await fetch("/api/payment/exchange-rate");
        if (rateResponse.ok) {
          const rateData = await rateResponse.json();
          setExchangeRate(rateData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCryptoPayment = async () => {
    setIsProcessing(true);

    try {
      // Если кошелек не подключен, подключаем его
      if (!wallet) {
        setPaymentStatus("connecting");
        await tonConnectUI.connectWallet();

        // Ждем подключения кошелька
        let attempts = 0;
        while (!tonConnectUI.wallet && attempts < 30) {
          // 30 секунд ожидания
          await new Promise((resolve) => setTimeout(resolve, 1000));
          attempts++;
        }
        if (!tonConnectUI.wallet) {
          throw new Error("Wallet connection failed");
        }
      }

      setPaymentStatus("processing");

      // Получаем адрес кошелька из .env через API
      const walletResponse = await fetch("/api/payment/wallet-address");
      if (!walletResponse.ok) {
        throw new Error("Failed to get wallet address");
      }
      const { walletAddress } = await walletResponse.json();

      // Рассчитываем сумму в TON
      const tonAmount = prices.usdt * exchangeRate.usdtToTonRate;
      const tonAmountNano = Math.floor(tonAmount * 1000000000); // TON имеет 9 десятичных знаков

      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 60 * 5, // 5 минут
        messages: [
          {
            address: walletAddress,
            amount: tonAmountNano.toString(), // Сумма в nanoTON
            // Убираем payload или делаем его пустым - TON не поддерживает произвольный текст
          },
        ],
      };

      const result = await tonConnectUI.sendTransaction(transaction);

      // Отправляем подтверждение на сервер
      const response = await fetch("/api/payment/confirm-crypto", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          telegram_id: user?.telegram_id,
          transaction_hash: result.boc,
          payment_method: "USDT_TON",
        }),
      });

      if (response.ok) {
        setPaymentStatus("success");
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setPaymentStatus("error");
      }
    } catch (error) {
      console.error("Crypto payment error:", error);
      setPaymentStatus("error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStarsPayment = async () => {
    setIsProcessing(true);
    setPaymentStatus("processing");

    try {
      // Проверяем, что мы в Telegram WebApp
      if (!window.Telegram?.WebApp) {
        throw new Error("This payment method is only available in Telegram");
      }

      const response = await fetch("/api/payment/create-stars-invoice", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          telegram_id: user?.telegram_id,
          plan_id: "lifetime",
          amount: prices.stars, // STARS_PRICE с сервера
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create Stars invoice");
      }

      const { invoice_link } = await response.json();

      // Открываем счет в Telegram
      window.Telegram.WebApp.openInvoice(invoice_link, (status) => {
        if (status === "paid") {
          setPaymentStatus("success");
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else if (status === "cancelled") {
          setPaymentStatus(null);
          setIsProcessing(false);
        } else {
          setPaymentStatus("error");
          setIsProcessing(false);
        }
      });
    } catch (error) {
      console.error("Stars payment error:", error);
      setPaymentStatus("error");
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Затемненный фон */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Модальное окно */}
      <div
        className="relative rounded-2xl p-4 mx-4 max-w-md w-full shadow-2xl overflow-hidden"
        style={{
          background: `
            linear-gradient(to bottom, 
              rgba(30, 30, 30, 0.60) 0%, 
              rgba(42, 42, 42, 0.65) 40%,
              rgba(30, 30, 30, 0.8) 70%,
              rgba(20, 20, 20, 0.9) 100%),
            url('/background-cosmic.png') center/cover no-repeat
          `,
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        {/* Floating particles (новая анимация) */}
        <style>{`
          .cgpeers-particle {
            position: absolute;
            width: 4px;
            height: 4px;
            background: rgba(102, 126, 234, 0.6);
            border-radius: 50%;
            pointer-events: none;
            animation: cgpeers-float 6s ease-in-out infinite;
          }
          @keyframes cgpeers-float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              transform: translateY(-100px) rotate(180deg);
              opacity: 0;
            }
          }
          .cgpeers-particle:nth-child(odd) {
            animation-delay: 2s;
            background: rgba(118, 75, 162, 0.6);
          }
          .cgpeers-particle:nth-child(3n) {
            animation-delay: 4s;
            background: rgba(255, 215, 0, 0.6);
          }
        `}</style>
        <div className="absolute inset-0 pointer-events-none">
          {/* 14 партиклов с рандомными позициями */}
          <div
            className="cgpeers-particle"
            style={{ top: "8%", left: "18%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "22%", left: "77%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "65%", left: "12%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "81%", left: "68%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "19%", left: "59%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "41%", left: "33%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "73%", left: "92%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "13%", left: "83%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "57%", left: "7%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "91%", left: "47%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "35%", left: "52%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "60%", left: "75%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "27%", left: "28%" }}
          />
          <div
            className="cgpeers-particle"
            style={{ top: "85%", left: "15%" }}
          />
        </div>

        {/* Кнопка закрытия */}
        <button
          onClick={onClose}
          className="absolute z-20 top-6 right-4 p-1 text-gray-400 hover:text-gray-300 transition-colors rounded-full hover:bg-white/10"
        >
          <X size={20} />
        </button>

        {/* Заголовок */}
        <div className="text-center mb-6">
          {/* Анимация glow для лого */}
          <style>{`
            @keyframes glow {
              from {
                filter: drop-shadow(0 0 10px rgba(102, 126, 234, 0.4));
              }
              to {
                filter: drop-shadow(0 0 30px rgba(102, 126, 234, 0.8));
              }
            }
          `}</style>
          <img
            src="/cgp_logo_app.png"
            alt="CGPeers"
            className="h-12 mx-auto mb-3 filter drop-shadow-lg"
            style={{
              animation: "glow 2s ease-in-out infinite alternate",
              filter: "drop-shadow(0 0 10px rgba(102, 126, 234, 0.3))",
            }}
          />
          <div>
            <style>{`
              @keyframes shine {
                0%, 100% {
                  transform: scale(1);
                  box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
                }
                50% {
                  transform: scale(1.05);
                  box-shadow: 0 0 30px rgba(255, 215, 0, 0.6);
                }
              }
            `}</style>
            <div
              className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide mb-4"
              style={{
                animation: "shine 3s ease-in-out infinite",
              }}
            >
              Premium Access
            </div>
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            Unlock Premium Features
          </h3>
          <p className="text-gray-300 text-sm">
            Get unlimited access to exclusive content, faster downloads, and
            priority support.
          </p>
        </div>

        {/* Список преимуществ */}
        <ul className="space-y-2 mb-6 text-sm">
          <li className="flex items-center text-gray-300">
            <span className="text-green-400 mr-2 font-bold">✓</span>
            <span>
              <strong>Full CGPeers library</strong> included
            </span>
          </li>
          <li className="flex items-center text-gray-300">
            <span className="text-green-400 mr-2 font-bold">✓</span>
            <span>
              One-time payment, <strong>no recurring fees</strong>
            </span>
          </li>
          <li className="flex items-center text-gray-300">
            <span className="text-green-400 mr-2 font-bold">✓</span>
            <span>
              <strong>Unlimited downloads</strong> & premium content
            </span>
          </li>
          <li className="flex items-center text-gray-300">
            <span className="text-green-400 mr-2 font-bold">✓</span>
            <span>
              Early access to <strong>new features</strong>
            </span>
          </li>
          <li className="flex items-center text-gray-300">
            <span className="text-green-400 mr-2 font-bold">✓</span>
            <span>
              <strong>Daily</strong> content updates
            </span>
          </li>
        </ul>

        {/* Статус оплаты */}
        {paymentStatus && (
          <div
            className={`mb-4 p-3 rounded-lg text-center text-sm ${
              paymentStatus === "success"
                ? "bg-green-900/50 text-green-300"
                : paymentStatus === "error"
                  ? "bg-red-900/50 text-red-300"
                  : paymentStatus === "connecting"
                    ? "bg-yellow-900/50 text-yellow-300"
                    : "bg-blue-900/50 text-blue-300"
            }`}
            style={{ position: "relative" }}
            id="payment-success-block"
          >
            {" "}
            {paymentStatus === "success" && (
              <>
                {"✅ Payment successful! Updating access..."}
                <ConfettiCanvas anchorSelector="#payment-success-block" />
              </>
            )}
            {paymentStatus === "error" && "❌ Payment error. Please try again."}
            {paymentStatus === "connecting" && "🔗 Connecting wallet..."}
            {paymentStatus === "processing" && "⏳ Processing payment..."}
          </div>
        )}

        {/* Кнопки оплаты */}
        <div className="space-y-2">
          {/* TON Payment Section - скрыто по умолчанию согласно политике Telegram */}
          {ENABLE_TON_PAYMENTS && (
            <div>
              {/* TON Price Info with extended background */}
              <div className="p-3 pt-3 pb-16 bg-gray-800/50 rounded-lg relative">
                <div className="text-center text-sm text-gray-300">
                  <div className="flex justify-between items-center">
                    <span>TON Payment:</span>
                    <div className="flex flex-col text-right">
                      <span className="text-orange-400 font-medium">
                        ${prices.usdt} USDT
                        <span className="text-orange-300 text-xs ml-2 align-middle">
                          ≈{" "}
                          {(prices.usdt * exchangeRate.usdtToTonRate).toFixed(
                            2,
                          )}{" "}
                          TON
                        </span>
                      </span>
                    </div>
                  </div>
                  {exchangeRate.fallback && (
                    <div className="mt-2 text-xs text-yellow-400">
                      * Using fallback exchange rate
                    </div>
                  )}
                </div>
                {/* TON Payment Button positioned absolutely */}
                <div className="absolute bottom-3 left-3 right-3">
                  <button
                    onClick={handleCryptoPayment}
                    disabled={isProcessing}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium py-3 px-4 pr-12 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 disabled:scale-100 shadow-lg"
                  >
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12s12-5.373 12-12S18.627 0 12 0M7.902 6.697h8.196c1.505 0 2.462 1.628 1.705 2.94l-5.059 8.765a.86.86 0 0 1-1.488 0L6.199 9.637c-.758-1.314.197-2.94 1.703-2.94m4.844 1.496v7.58l1.102-2.128l2.656-4.756a.465.465 0 0 0-.408-.696zM7.9 8.195a.464.464 0 0 0-.408.694l2.658 4.754l1.102 2.13V8.195z" />
                    </svg>
                    {isProcessing && paymentStatus === "connecting"
                      ? "Connecting Wallet..."
                      : "Pay with TON"}
                  </button>

                  {/* Wallet Icon */}
                  <button
                    onClick={() => {
                      if (wallet) {
                        tonConnectUI.disconnect();
                      }
                    }}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-md transition-colors ${
                      wallet
                        ? "text-green-400 hover:text-green-300"
                        : "text-gray-400 hover:text-gray-300"
                    }`}
                    title={
                      wallet ? "Disconnect wallet" : "Wallet not connected"
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fill="currentColor"
                        d="M14.5 4H2.38a1 1 0 0 1-1.19-.982v-.019L14 2.5V1.31A1.18 1.18 0 0 0 12.684.001L1.31 1.85A2.004 2.004 0 0 0 0 3.727v10.772a1.5 1.5 0 0 0 1.5 1.5h13a1.5 1.5 0 0 0 1.5-1.5v-9.01l.001-.041a1.45 1.45 0 0 0-1.45-1.45l-.053.001zM13 11a1.5 1.5 0 1 1 .001-3.001A1.5 1.5 0 0 1 13 11"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Telegram Stars Section */}
          <div>
            {/* Stars Price Info with extended background */}
            <div className="p-3 pt-3 pb-16 bg-gray-800/50 rounded-lg relative">
              <div className="text-center text-sm text-gray-300">
                <div className="flex justify-between items-center">
                  <span>Telegram Stars:</span>
                  <span className="text-blue-400 font-medium">
                    {prices.stars} ⭐
                  </span>
                </div>
              </div>

              {/* Telegram Stars Payment Button positioned absolutely */}
              <div className="absolute bottom-3 left-3 right-3">
                <button
                  onClick={handleStarsPayment}
                  disabled={isProcessing}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 disabled:scale-100 shadow-lg"
                >
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="m11.47 18.6-4.5 2.73a1 1 0 0 1-1.37-.32.98.98 0 0 1-.12-.76l.7-2.73c.25-.98.93-1.8 1.85-2.24l4.91-2.34a.46.46 0 0 0 .22-.61.46.46 0 0 0-.5-.25l-5.46.94a3.85 3.85 0 0 1-3.12-.84l-1.72-1.43A.98.98 0 0 1 2.92 9l5.27-.41a1 1 0 0 0 .84-.61l2.04-4.88a1 1 0 0 1 1.83 0l2.04 4.88a1 1 0 0 0 .84.6l5.3.42a.99.99 0 0 1 .57 1.73l-4.04 3.43a.98.98 0 0 0-.33.98l1.25 5.13a.99.99 0 0 1-.74 1.2 1 1 0 0 1-.75-.13L12.5 18.6a1 1 0 0 0-1.03 0Z" />
                  </svg>
                  Pay with Telegram Stars
                </button>
              </div>
            </div>
          </div>{" "}
        </div>

        {/* Поддержка */}
        <div className="text-center mt-2 text-xs text-gray-400">
          Have questions? Message us at{" "}
          <a
            href="https://t.me/Cgpeers_invites"
            className="text-blue-400 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            @Cgpeers_invites
          </a>
        </div>
      </div>
    </div>
  );
};

export default LifetimePremiumModal;
